import type { ProductGroup as BaseProductGroup, ProductGroupItem as BaseProductGroupItem } from '@xpanse/organization-service-client';

import { useProductGroupsApi } from '@/api/organizations';
import { useErrorNotification } from '@/hooks/useErrorNotification';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';


// Extend the types to match the actual API response
export interface ProductGroupItem extends Omit<BaseProductGroupItem, 'productName'> {
  productName?: string;
}

export interface ProductGroup extends Omit<BaseProductGroup, 'products'> {
  default?: boolean;
  products?: ProductGroupItem[];
}

// Provider interface for the getProviders endpoint
export interface Provider {
  id: number;
  productCategoryId: number;
  productGroupId: number;
  mortgageProductId: number | null;
  productCode: string;
  productName: string;
  productDescription: string;
  productType: string;
  price: number;
  includeInListing: boolean;
  clickFees: Record<string, any>;
  statePrice: Record<string, any>;
  estimatedReturnTimeUnit: string;
  estimatedReturnTimeValue: number;
  requestCancellationSupported: boolean;
  vendorId: number;
  vendorCode: string;
  vendorName?: string;
  productSchemaId: number;
  tags: string[];
  allowedStates: string[];
  forbiddenStates: string[];
}

// Request interface for saving product groups
export interface SaveProductGroupRequest {
  id?: number; // Optional, omit for new product groups
  name: string;
  vendorCode: string | null;
  productCode: string | null;
  productGroupType: 'CASCADE';
  active: boolean;
  products: {
    providerId: number;
    vendorCode: string;
    productCode: string;
    sortOrder: number;
    active: boolean;
  }[];
  default?: boolean;
}

const queryKey = () => ['product-groups'];
const providersQueryKey = (tag: string) => ['product-groups', 'providers', tag];

export const useGetProductGroups = () => {
  const productGroupsApi = useProductGroupsApi();

  return useQuery<ProductGroup[], Error>({
    queryKey: queryKey(),
    queryFn: async () => {
      try {
        const response = await (productGroupsApi as any).request({
          path: '/product-groups/list',
          method: 'GET',
        });
        return response.json();
      } catch (error) {
        console.error('[useProductGroups] Error details:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
          name: error instanceof Error ? error.name : undefined,
        });
        throw error;
      }
    },
  });
};

export const useSetDefaultProductGroup = () => {
  const productGroupsApi = useProductGroupsApi();
  const queryClient = useQueryClient();
  const { reportError } = useErrorNotification();

  return useMutation<ProductGroup, Error, number>({
    mutationFn: async (productGroupId: number) => {
      console.log('[useSetDefaultProductGroup] Setting default for ID:', productGroupId);

      try {
        const response = await (productGroupsApi as any).request({
          path: `/product-groups/setDefault/${productGroupId}`,
          method: 'PUT',
        });

        console.log('[useSetDefaultProductGroup] Response:', response);
        const data = await response.json();
        console.log('[useSetDefaultProductGroup] Success, data:', data);

        return data;
      } catch (error) {
        console.error('[useSetDefaultProductGroup] Error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate and refetch the product groups list
      console.log('[useSetDefaultProductGroup] Invalidating query cache');
      queryClient.invalidateQueries({ queryKey: queryKey() });
    },
    onError: (error) => {
      console.error('[useSetDefaultProductGroup] Mutation error:', error);
      reportError(error);
    },
  });
};

export const useGetAllProviders = (enabled = true) => {
  const productGroupsApi = useProductGroupsApi();

  return useQuery<Provider[], Error>({
    queryKey: ['product-groups', 'providers', 'all'],
    queryFn: async () => {
      const response = await (productGroupsApi as any).request({
        path: '/product-groups/getProviders',
        method: 'GET',
      });
      return response.json();
    },
    enabled,
  });
};

export const useGetProviders = (category: string) => {
  const productGroupsApi = useProductGroupsApi();

  return useQuery<Provider[], Error>({
    queryKey: providersQueryKey(category),
    queryFn: async () => {
      try {
        const response = await (productGroupsApi as any).request({
          path: `/product-groups/getProviders?tag=${encodeURIComponent(category)}`,
          method: 'GET',
        });
        return response.json();
      } catch (error) {
        console.error('[useGetProviders] Error:', error);
        throw error;
      }
    },
    enabled: !!category, // Only run query if tag is provided
  });
};

export const useDeleteProductGroup = () => {
  const productGroupsApi = useProductGroupsApi();
  const queryClient = useQueryClient();
  const { reportError } = useErrorNotification();

  return useMutation<void, Error, number>({
    mutationFn: async (productGroupId: number) => {
      console.log('[useDeleteProductGroup] Deleting product group:', productGroupId);
      await (productGroupsApi as any).request({
        path: `/product-groups/${productGroupId}`,
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey() });
    },
    onError: (error) => {
      console.error('[useDeleteProductGroup] Error:', error);
      reportError(error);
    },
  });
};

export const useSaveProductGroup = () => {
  const productGroupsApi = useProductGroupsApi();
  const queryClient = useQueryClient();
  const { reportError } = useErrorNotification();

  return useMutation<ProductGroup, Error, SaveProductGroupRequest>({
    mutationFn: async (request: SaveProductGroupRequest) => {
      console.log('[useSaveProductGroup] Saving product group:', request);

      try {
        const payload = { ...request };
        if (!payload.id || payload.id === 0) {
          delete payload.id;
        }

        console.log('[useSaveProductGroup] Payload object:', payload);
        console.log('[useSaveProductGroup] POST /product-groups/save body (JSON):', JSON.stringify(payload));

        const response = await (productGroupsApi as any).request({
          path: '/product-groups/save',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          // IMPORTANT: pass object; runtime serializes JSON once.
          body: payload,
        });

        const raw = await response.text();
        console.log('[useSaveProductGroup] Response status/url:', response.status, response.url);
        console.log('[useSaveProductGroup] Response body raw:', raw);

        if (!raw) return payload as unknown as ProductGroup;

        try {
          return JSON.parse(raw) as ProductGroup;
        } catch {
          return payload as unknown as ProductGroup;
        }
      } catch (error) {
        console.error('[useSaveProductGroup] Error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log('[useSaveProductGroup] Invalidating query cache');
      queryClient.invalidateQueries({ queryKey: queryKey() });
    },
    onError: (error) => {
      console.error('[useSaveProductGroup] Mutation error:', error);
      reportError(error);
    },
  });
};
